import { getTodayYMD } from "./dateUtils";
import {
  readPartnerIsAvailableFromPayload,
  readPartnerIsCheckedInTodayFromPayload,
} from "./partnerAvailability";

const LAST_ATTENDANCE_DATE_KEY = "dpLastAttendanceActionDate";
const LAST_ATTENDANCE_ACTION_KEY = "dpLastAttendanceAction";

export type AttendanceAction = "in" | "out";

export function persistAttendanceAction(action: AttendanceAction): void {
  localStorage.setItem(LAST_ATTENDANCE_DATE_KEY, getTodayYMD());
  localStorage.setItem(LAST_ATTENDANCE_ACTION_KEY, action);
}

/**
 * UI check-in state: after midnight (new calendar day) always show Check-in
 * until the partner checks in today, regardless of stale is_available from API.
 */
export function resolvePartnerCheckedInForUi(
  apiAvailable: boolean | null
): boolean {
  const today = getTodayYMD();
  const lastDate = localStorage.getItem(LAST_ATTENDANCE_DATE_KEY);
  const lastAction = localStorage.getItem(LAST_ATTENDANCE_ACTION_KEY);

  if (lastDate !== today) {
    return false;
  }
  if (lastAction === "in") return true;
  if (lastAction === "out") return false;
  return apiAvailable === true;
}

export function readPartnerCheckedInFromProfileStorage(): boolean | null {
  try {
    const raw = localStorage.getItem("profile");
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;

    // Prefer the server-derived day-aware flag when available.
    const today = readPartnerIsCheckedInTodayFromPayload(p);
    if (today !== null) {
      // Day-aware server signal already accounts for midnight reset. We still
      // honor the local optimistic "checked out today" override if the user
      // just checked out and the cached profile hasn't refreshed yet.
      const lastDate = localStorage.getItem(LAST_ATTENDANCE_DATE_KEY);
      const lastAction = localStorage.getItem(LAST_ATTENDANCE_ACTION_KEY);
      if (lastDate === getTodayYMD() && lastAction === "out") return false;
      return today;
    }

    // Backward compatibility for older payloads without `is_checked_in_today`.
    const api = readPartnerIsAvailableFromPayload(p);
    return resolvePartnerCheckedInForUi(api);
  } catch {
    return null;
  }
}

/** Call when the calendar day changes so checkout UI becomes check-in. */
export function applyMidnightAttendanceReset(
  onAvailability?: (checkedIn: boolean) => void
): void {
  onAvailability?.(false);
}

/**
 * Detects local midnight / date rollover while the app is open.
 */
export function subscribeMidnightAttendanceReset(
  onDayChange: () => void
): () => void {
  let lastYmd = getTodayYMD();

  const check = () => {
    const today = getTodayYMD();
    if (today !== lastYmd) {
      lastYmd = today;
      onDayChange();
    }
  };

  const intervalId = window.setInterval(check, 30_000);
  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onVisible);
  check();

  return () => {
    window.clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
